import { useEffect, useRef } from 'react'

/** Returns a float in [0, 1) using the Web Crypto API. */
function secureRandom(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0] / 0x100000000
}

export default function MatrixRain() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas to full screen
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight

    // Matrix characters - katakana, latin letters, numbers, and gridX
    const matrixChars = 'ｦｱｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZgridX'
    const gridXText = 'gridX'

    const fontSize = 16
    const columns = Math.floor(canvas.width / fontSize)

    // Array to store y position of each column
    const drops: number[] = []
    // Track which columns should show gridX
    const gridXColumns: boolean[] = []

    // Initialize drops at random positions
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.floor(secureRandom() * -100)
      // Randomly assign some columns to show gridX (about 10% of columns)
      gridXColumns[i] = secureRandom() < 0.1
    }

    let gridXCounter = 0

    const draw = () => {
      // Semi-transparent black background for trail effect
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      ctx.font = `${fontSize}px monospace`

      for (let i = 0; i < drops.length; i++) {
        // Check if this column should display gridX
        const shouldShowGridX = gridXColumns[i] && drops[i] % 7 === 0

        let text: string
        if (shouldShowGridX && gridXCounter % 5 === 0) {
          // Show gridX text
          text = gridXText[Math.floor(secureRandom() * gridXText.length)]
          // Brighter green for gridX
          ctx.fillStyle = 'hsl(120, 100%, 70%)'
        } else {
          // Random character from matrix chars
          text = matrixChars[Math.floor(secureRandom() * matrixChars.length)]
          // Normal matrix green
          ctx.fillStyle = 'hsl(120, 100%, 50%)'
        }

        const x = i * fontSize
        const y = drops[i] * fontSize

        ctx.fillText(text, x, y)

        // Reset drop to top randomly after it falls off screen
        if (y > canvas.height && secureRandom() > 0.975) {
          drops[i] = 0
        }

        drops[i]++
      }

      gridXCounter++
    }

    // Animation loop
    const interval = setInterval(draw, 50)

    // Handle window resize
    const handleResize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }

    window.addEventListener('resize', handleResize)

    return () => {
      clearInterval(interval)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.3 }}
    />
  )
}